import { LitElement, html, nothing } from '../../../vendor/lit/lit-core.esm.js?v=20260730a';

// Reusable modal shell — the "Modal-Gerüst" component identified in the
// framework-evaluation memo (2026-07-30): 15 modals across this codebase
// hand-roll the same backdrop/header/tabs/body lifecycle. This is the first
// one extracted, piloted on
// print_settings_admin_modal.js.
//
// Renders into LIGHT DOM on purpose (no Shadow Root, see createRenderRoot()
// below) — a deliberate choice for incremental adoption: every existing
// .modal/.user-admin-*/.admin-* rule in styles.css keeps applying unchanged,
// with zero CSS duplicated into the component. Encapsulated Shadow DOM +
// component-scoped styles is a reasonable next step once more of the app
// has migrated and true style isolation is worth the duplication cost — not
// needed to validate the pattern itself.
//
// Usage:
//   html`<ks-modal
//     .modalTitle=${'...'} .subtitle=${'...'} .closeLabel=${'...'}
//     .tabsTemplate=${html`...optional tab bar...`}
//     .bodyTemplate=${html`...panel content...`}
//     .extraActionsTemplate=${html`...optional extra header buttons, rendered after Schliessen...`}
//     @ks-modal-close=${...}
//   ></ks-modal>`
// tabsTemplate/bodyTemplate take Lit TemplateResults (not light-DOM
// children!) rather than <slot> — <slot> only redistributes content across a
// Shadow Root boundary, and this component deliberately has none (see
// createRenderRoot() below), so a light-DOM <slot> would just be inert
// markup. Passing pre-rendered TemplateResults as properties composes
// correctly into this component's own render() either way, and is the
// standard fallback pattern for light-DOM-rendering Lit components — the
// same lever every other "insert this parent's content into a child's
// template" component here will need until a real reason for full Shadow
// DOM encapsulation shows up.
// Listen for the `ks-modal-close` event from the built-in close button to
// remove the element from the DOM. Backdrop clicks and Escape deliberately do
// not close modals: text selection and pointer drags may end on the backdrop.
export class KsModal extends LitElement {
  static properties = {
    modalTitle: { attribute: false },
    subtitle: { attribute: false },
    message: { attribute: false },
    messageError: { type: Boolean },
    closeLabel: { attribute: false },
    modalClass: { attribute: false },
    tabsTemplate: { attribute: false },
    bodyTemplate: { attribute: false },
    extraActionsTemplate: { attribute: false }
  };

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.modalTitle = '';
    this.subtitle = '';
    this.message = '';
    this.messageError = false;
    this.closeLabel = 'Schliessen';
    this.modalClass = '';
    this.tabsTemplate = nothing;
    this.bodyTemplate = nothing;
    this.extraActionsTemplate = nothing;
  }

  // Arrow-function fields, not prototype methods: safe to hand to a future
  // caller as an event value the same way tabsTemplate/bodyTemplate get
  // rendered here, without depending on which component's render() ends up
  // walking the template (see the long comment in print_settings_admin_modal.js
  // for the exact failure mode this avoids).
  _close = () => {
    this.dispatchEvent(new CustomEvent('ks-modal-close', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <div class="modal-backdrop auth-modal-root bom-modal-root user-admin-root">
        <div class="modal bom-modal user-admin-modal ${this.modalClass}" role="dialog" aria-modal="true" aria-label=${this.modalTitle}>
          <div class="user-admin-header">
            <div>
              <div class="modal-title">${this.modalTitle}</div>
              ${this.subtitle ? html`<div class="small-text">${this.subtitle}</div>` : nothing}
            </div>
            <div class="user-admin-actions">
              <button class="btn" type="button" @click=${this._close}>${this.closeLabel}</button>
              ${this.extraActionsTemplate}
            </div>
          </div>
          ${this.tabsTemplate}
          <div class="user-admin-body">
            <div class="small-text admin-message${this.messageError ? ' is-error' : ''}" aria-live="polite">${this.message}</div>
            ${this.bodyTemplate}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('ks-modal', KsModal);
